import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';

import EpisodesListItem from '@/components/EpisodesListItem.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { EpisodeFragment } from '@/pages/graphql/fragments/Episode.fragment';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import { useQuery } from '@/helpers/composables/useApollo';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { GetSeasonEpisodesDocument, GetSeasonEpisodesQuery } from '@/pages/graphql/queries/GetSeasonEpisodes.query';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useI18n } from '@/helpers/composables/useI18n';
import { useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import { BuyBoxFilterOptions } from '@/stores/user.store';


export default /*#__PURE__*/_defineComponent({
  __name: 'EpisodesList',
  props: {
    title: null,
    titleId: null,
    titleObjectId: null,
    titlePosterUrl: null,
    titleName: null,
    episodes: null,
    showSeenProgress: { default: 0 },
    isTitleInWatchlist: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    sponsoredAd: { default: undefined },
    maxEpisodes: null
  },
  setup(__props: any) {

const props = __props as { title: TitleDetail, titleId: string, titleObjectId: number, titlePosterUrl?: string, titleName?: string, episodes: EpisodeFragment[], showSeenProgress: number, isTitleInWatchlist: boolean, additionalContexts: SnowplowContext[], sponsoredAd: SponsoredAdFragment | undefined, maxEpisodes: number };



const { country, language } = useLanguageStore();
const { t } = useI18n();

const { allRegularOffers } = useBuyBoxData({
	title: () => props.title,
	buyboxFilter: BuyBoxFilterOptions.ALL,
});

const limit = computed(() => (allRegularOffers.value.length > 0 ? 20 : 5));
const offset = ref(limit.value);
const enabled = computed(() => !!props.title.id && props.title.__typename === 'Season');

const { result, onError, fetchMore, loading } = useQuery<GetSeasonEpisodesQuery>(
	GetSeasonEpisodesDocument,
	() => ({
		country: country.value,
		language: language.value,
		limit: limit.value,
		nodeId: props.title.id,
		offset: 0,
	}),
	{
		enabled,
		errorPolicy: 'all',
	}
);

const episodesList = computed(() => {
	if (!enabled.value || !result.value) {
		return props.episodes;
	}

	// Season pages can have more than 20 episodes, so we need replicate the query so we can fetch more with pagination
	// When we would fetch more on the title query the whole page is reloaded.
	return result.value?.node?.__typename === 'Season' ? result.value.node.episodes : props.episodes;
});

const buttonText = computed(() =>
	props.maxEpisodes > episodesList.value.length + limit.value
		? t('WEBAPP_LOAD_MORE')
		: t('WEBAPP_EPISODELIST_MORE', { maxEpisodes: props.maxEpisodes })
);

onError(error => {
	captureMessageForSentry('[GraphQL] GetSeasonEpisodes', { error, where: 'Component: EpisodesList.vue' }, 'error');
});

function onLoadMore() {
	fetchMore({
		variables: {
			limit: limit.value,
			offset: offset.value,
		},
		updateQuery: (previousResult, { fetchMoreResult }) => {
			if (fetchMoreResult?.node?.__typename === 'Season' && previousResult?.node?.__typename === 'Season') {
				previousResult.node.episodes = [...previousResult.node.episodes, ...fetchMoreResult.node.episodes];
			}
			return previousResult;
		},
	});

	offset.value += limit.value;
}

function loadMore() {
	onLoadMore();

	TrackingHelper.trackEvent('userinteraction', {
		action: 'click',
		label: 'load_more_episodes',
	});
}

function getEpisodesListItemProps(episode: EpisodeFragment) {
	return {
		title: props.title,
		episode: episode,
		showSeenProgress: props.showSeenProgress,
		isTitleInWatchlist: props.isTitleInWatchlist,
		titleId: props.titleId,
		titleObjectId: props.titleObjectId,
		titleName: props.titleName ?? '',
		additionalContexts: props.additionalContexts,
		sponsoredAd: props.sponsoredAd,
	};
}

return { __sfc: true,props, country, language, t, allRegularOffers, limit, offset, enabled, result, onError, fetchMore, loading, episodesList, buttonText, onLoadMore, loadMore, getEpisodesListItemProps, EpisodesListItem, BasicButton }
}

})